// Server-safe product catalog — no image imports (used by API routes / Node.js)
'use strict';

const PRODUCT_CATALOG = {
  'fully-automated-grafting-robot': {
    id: 'fully-automated-grafting-robot',
    name: 'Fully Automated Grafting Robot',
    price: 749999,
    currency: 'INR',
    description:
      "India's first fully indigenously developed robotic grafting platform with adaptable automation levels — from semi-automated manual feeding to a fully automated pick-and-place workflow. Built for nurseries that need higher graft throughput with a single operator.",
    purchasable: false,
  },
  'easygraft-143': {
    id: 'easygraft-143',
    name: 'EasyGraft',
    price: 399,
    currency: 'INR',
    shippingFee: 100,
    description:
      'EasyGraft is a precision pocket vegetable grafting tool for tomatoes, brinjal, and cucurbits. Angle adjustment up to 120°, replaceable standard razor blades, and PETG-CF strength. Hold, align, and cut.',
    features: [
      'Precision angle cuts for optimal grafting',
      'Ergonomic design for comfortable use',
      'Portable and easy to maintain',
      'Angle adjustable up to 120 degrees',
      'Easily replaceable standard razor blades',
      'Made with strong PETG-CF material, giving ultimate strength and long-term usage',
    ],
    purchasable: true,
  },
  'iot-controlled-humidifier-mist-generator': {
    id: 'iot-controlled-humidifier-mist-generator',
    name: 'IoT Controlled Humidifier (Mist Generator)',
    price: 4999,
    currency: 'INR',
    description:
      'IoT mist generator controller for nurseries, greenhouses, and hydroponics. Remote app control from anywhere, timer scheduling, data export, and optional CO2, temperature, humidity, and lux sensors. From ₹4,999.',
    features: [
      '4-channel mist generator control for flexible humidification setups',
      'Attachment support for a 5V fan to help control humidity',
      'Temperature, humidity, CO2, and lux meter circuit support',
      'LED light integration for indoor grow and nursery applications',
      'Connect up to 3 I2C sensors for expanded environmental monitoring',
      'Works with USB-C and barrel jack power inputs',
      'Timer-based on/off scheduling for automated mist control',
      'Free application for remote monitoring and device control from anywhere',
      'Ability to export sensor and device data',
    ],
    applications: [
      'Agricultural nurseries',
      'Hydroponics farms',
      'Indoor fragrance diffusion',
      'Reducing indoor pollutants',
      'Indoor plant care',
    ],
    variants: [
      {
        id: 'controller',
        name: 'Controller Only',
        price: 4999,
      },
      {
        id: 'co2-temperature-humidity',
        name: 'CO2, Temperature and Humidity Variant',
        price: 7999,
      },
      {
        id: 'co2-temperature-humidity-lux',
        name: 'CO2, Temperature, Humidity and Lux Meter Variant',
        price: 8999,
      },
    ],
    casingOptions: [
      {
        id: 'without-casing',
        name: 'Without Casing',
        price: 0,
      },
      {
        id: 'with-casing',
        name: 'With Casing',
        price: 500,
      },
    ],
    purchasable: false,
  },
  'canstepper-adapter-board': {
    id: 'canstepper-adapter-board',
    name: 'CANStepper Closed-Loop NEMA 17 CAN Bus Controller',
    price: 2899,
    currency: 'INR',
    description:
      'Closed-loop NEMA 17 controller with CAN Bus: TMC2209 driver, 14-bit encoder and ESP32-C3, commanded from Python over CAN or USB. Board only or board with NEMA 17 stepper motor.',
    features: [
      'Closed-loop NEMA 17 stepper control with onboard magnetic encoder feedback',
      'TMC2209 silent stepper driver with microstepping and current control',
      'CAN Bus daisy-chain for multi-axis distributed motor control',
    ],
    variants: [
      {
        id: 'board-only',
        name: 'Board Only',
        price: 2899,
      },
      {
        id: 'board-with-motor',
        name: 'Board + NEMA 17 Stepper Motor',
        price: 3699,
      },
    ],
    purchasable: true,
  },
};

const USD_PER_INR = 0.01048;
const INTL_USD_MULTIPLIER = 1.05;
const DEFAULT_DISPLAY_DISCOUNT_PERCENT = 30;
const DEFAULT_FLASH_DISCOUNT_PERCENT = 5;
const DEFAULT_INTL_SHIPPING_USD = 9;
// Must match the per-line quantity limit enforced by shop-create-order.
const MAX_QUANTITY_PER_ITEM = 30;
// Bulk (robotic-arm build) discount; must match shop-create-order.
const BULK_DISCOUNT_MIN_QTY = 6;
const BULK_DISCOUNT_PERCENT = 5;

// Live pricing rows from the product_pricing table (managed in the admin
// dashboard). Loaded at runtime by PricingProvider; everything below falls
// back to the hardcoded catalog values until (or unless) rows arrive.
let LIVE_PRICING = {};

function setLivePricing(rows) {
  const next = {};
  (rows || []).forEach((row) => {
    if (row && row.product_id) next[row.product_id] = row;
  });
  LIVE_PRICING = next;
}

function livePricingFor(productId) {
  return (productId && LIVE_PRICING[productId]) || null;
}

function positiveOrNull(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function nonNegativeOrNull(value) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function displayDiscountPercentFor(productId) {
  const pct = nonNegativeOrNull(livePricingFor(productId)?.display_discount_percent);
  return pct === null ? DEFAULT_DISPLAY_DISCOUNT_PERCENT : Math.min(90, pct);
}

function flashDiscountPercentFor(productId) {
  const pct = nonNegativeOrNull(livePricingFor(productId)?.flash_discount_percent);
  return pct === null ? DEFAULT_FLASH_DISCOUNT_PERCENT : Math.min(90, pct);
}

function basePriceFor(productId, fallback) {
  const fb = Number(fallback);
  return positiveOrNull(livePricingFor(productId)?.base_price) ?? (Number.isFinite(fb) ? fb : 0);
}

function variantPriceFor(productId, variantId, fallback) {
  const fb = Number(fallback);
  return (
    positiveOrNull(livePricingFor(productId)?.variant_prices?.[variantId]) ??
    (Number.isFinite(fb) ? fb : 0)
  );
}

function casingPriceFor(productId, casingId, fallback) {
  return nonNegativeOrNull(livePricingFor(productId)?.casing_prices?.[casingId]) ?? (Number(fallback) || 0);
}

/** Unit price (INR) for a product / variant / casing selection with live overrides. */
function unitPriceFor(productId, variantId, casingId, fallback) {
  const product = PRODUCT_CATALOG[productId];
  if (!product) return Number(fallback) || 0;
  const variant = variantId
    ? (product.variants || []).find((v) => v.id === variantId)
    : null;
  const casing = casingId
    ? (product.casingOptions || []).find((c) => c.id === casingId)
    : null;
  let price = variant
    ? variantPriceFor(productId, variantId, variant.price)
    : basePriceFor(productId, product.price);
  if (casing) price += casingPriceFor(productId, casingId, casing.price);
  if (!Number.isFinite(price) || price <= 0) return Number(fallback) || 0;
  return price;
}

function shippingFeeInrFor(productId, fallback) {
  const liveFee = nonNegativeOrNull(livePricingFor(productId)?.shipping_fee_inr);
  return liveFee ?? (Number(fallback) || 0);
}

function intlShippingUsdFor(productId) {
  return nonNegativeOrNull(livePricingFor(productId)?.intl_shipping_usd) ?? DEFAULT_INTL_SHIPPING_USD;
}

/** Most units of one product a buyer may order: the server cap, lowered by `stock` when set. */
function maxQuantityFor(productId) {
  const stock = Number(PRODUCT_CATALOG[productId]?.stock);
  if (Number.isFinite(stock) && stock >= 0) {
    return Math.min(MAX_QUANTITY_PER_ITEM, Math.floor(stock));
  }
  return MAX_QUANTITY_PER_ITEM;
}

function shippingFeeForItems(items) {
  return (items || []).reduce((fee, item) => {
    const productFee = shippingFeeInrFor(
      item.productId,
      Number(PRODUCT_CATALOG[item.productId]?.shippingFee) || 0
    );
    return Math.max(fee, productFee);
  }, 0);
}

function inrToUsd(inrAmount) {
  const usd = Number(inrAmount) * USD_PER_INR * INTL_USD_MULTIPLIER;
  if (!Number.isFinite(usd) || usd <= 0) return 0;
  return Math.ceil(Math.round(usd * 100) / 100);
}

function compareAtInr(inrAmount, productId) {
  const n = Number(inrAmount);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.ceil(n * (1 + displayDiscountPercentFor(productId) / 100));
}

function sellingInr(inrAmount, flashDiscount, productId) {
  const n = Number(inrAmount);
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (!flashDiscount) return n;
  return Math.round(n * (1 - flashDiscountPercentFor(productId) / 100));
}

// PayPal / international only. Cashfree India shipping is unchanged.
// Per-product fee from product_pricing (default $9); the cart pays the highest
// fee across items. A product may define a bulk rule (e.g. Board + NEMA 17:
// $45 for 5 or more units), optionally scoped to one variant.
function paypalShippingFeeForItems(items) {
  const lineItems = items || [];
  let fee = 0;
  for (const item of lineItems) {
    const row = livePricingFor(item.productId);
    let itemFee = row ? intlShippingUsdFor(item.productId) : DEFAULT_INTL_SHIPPING_USD;
    if (row) {
      const bulkFee = nonNegativeOrNull(row.intl_bulk_shipping_usd);
      const bulkQty = positiveOrNull(row.intl_bulk_min_qty);
      const variantMatches = !row.intl_bulk_variant_id || row.intl_bulk_variant_id === item.variantId;
      if (bulkFee !== null && bulkQty !== null && variantMatches && Number(item.quantity) >= bulkQty) {
        itemFee = Math.max(itemFee, bulkFee);
      }
    } else if (item.productId === 'canstepper-adapter-board' && item.variantId === 'board-with-motor') {
      // Static-catalog fallback keeps the historical bulk rule.
      itemFee = Math.max(itemFee, Number(item.quantity) >= 5 ? 45 : 9);
    }
    fee = Math.max(fee, itemFee);
  }
  return lineItems.length ? fee : 0;
}

function bulkDiscountApplies(productQuantity) {
  return Number(productQuantity) >= BULK_DISCOUNT_MIN_QTY;
}

/** Unit price (INR) after the bulk discount, given the product's total quantity in the order. */
function bulkDiscountedInr(unitInr, productQuantity) {
  const n = Number(unitInr);
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (!bulkDiscountApplies(productQuantity)) return n;
  return Math.round(n * (1 - BULK_DISCOUNT_PERCENT / 100));
}

/** USD unit price after the bulk discount, kept to whole cents for PayPal. */
function bulkDiscountedUsd(unitUsd, productQuantity) {
  const n = Number(unitUsd);
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (!bulkDiscountApplies(productQuantity)) return n;
  return Math.round(n * (100 - BULK_DISCOUNT_PERCENT)) / 100;
}

function roundCents(amount) {
  return Math.round(Number(amount) * 100) / 100;
}

/** Total units per product id across all lines (variants of one product count together). */
function quantitiesByProduct(items) {
  const totals = {};
  for (const item of items || []) {
    totals[item.productId] = (totals[item.productId] || 0) + (Number(item.quantity) || 0);
  }
  return totals;
}

function paypalTotalsForItems(items, flashDiscount = false) {
  const lineItems = items || [];
  const productQty = quantitiesByProduct(lineItems);
  const subtotal = roundCents(
    lineItems.reduce(
      (sum, item) =>
        sum +
        bulkDiscountedUsd(
          inrToUsd(sellingInr(item.price, flashDiscount, item.productId)),
          productQty[item.productId]
        ) *
          Number(item.quantity || 0),
      0
    )
  );
  const shipping = lineItems.length ? paypalShippingFeeForItems(lineItems) : 0;
  return {
    subtotal,
    shipping,
    total: subtotal + shipping,
  };
}

module.exports = {
  PRODUCT_CATALOG,
  USD_PER_INR,
  MAX_QUANTITY_PER_ITEM,
  BULK_DISCOUNT_MIN_QTY,
  BULK_DISCOUNT_PERCENT,
  bulkDiscountApplies,
  bulkDiscountedInr,
  bulkDiscountedUsd,
  roundCents,
  quantitiesByProduct,
  maxQuantityFor,
  setLivePricing,
  livePricingFor,
  displayDiscountPercentFor,
  flashDiscountPercentFor,
  basePriceFor,
  variantPriceFor,
  casingPriceFor,
  unitPriceFor,
  shippingFeeInrFor,
  intlShippingUsdFor,
  shippingFeeForItems,
  inrToUsd,
  compareAtInr,
  sellingInr,
  paypalShippingFeeForItems,
  paypalTotalsForItems,
};
